import { isQueryWorksheetNode } from "@/components/query/sql/worksheets/worksheet-files";
import {
  listWorksheetNodes,
  listWorksheetSpaces,
} from "@/gen_src/proto/chalk/server/v1/worksheets-WorksheetsService_connectquery";
import { WorksheetNode, WorksheetSpace } from "@/gen_src/proto/chalk/server/v1/worksheets_pb";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { createQueryOptions, useQuery, useTransport } from "@connectrpc/connect-query";
import { useQueries } from "@tanstack/react-query";

export const getVisibleSqlWorksheetCount = (
  spaces: readonly WorksheetSpace[] | undefined,
  nodesBySpace: readonly (readonly WorksheetNode[] | undefined)[],
): number | undefined => {
  if (
    spaces == null ||
    spaces.length !== nodesBySpace.length ||
    nodesBySpace.some((nodes) => nodes == null)
  ) {
    return undefined;
  }

  return nodesBySpace.reduce(
    (total, nodes) => total + (nodes?.filter(isQueryWorksheetNode).length ?? 0),
    0,
  );
};

/**
 * Counts the same visible Shared and Private worksheet lists rendered by the SQL explorer. Pass
 * `enabled: false` to skip the requests where nothing shows the count.
 */
export const useSqlWorksheetCount = ({ enabled = true }: { enabled?: boolean } = {}):
  | number
  | undefined => {
  const { environmentId } = useRouteAndTeamInfo();
  const transport = useTransport();
  const { data: spacesData } = useQuery(
    listWorksheetSpaces,
    {},
    { enabled: enabled && environmentId != null },
  );
  const spaces = spacesData?.spaces;
  const nodeQueries = useQueries({
    queries: (spaces ?? []).map((space) =>
      createQueryOptions(
        listWorksheetNodes,
        { spaceId: space.id, includeArchived: false },
        { transport },
      ),
    ),
  });

  return getVisibleSqlWorksheetCount(
    spaces,
    nodeQueries.map((query) => query.data?.nodes),
  );
};
